import { defineQueries, defineQuery, } from "@rocicorp/zero";
import z from "zod";
import { zql } from "./schema.js";
/**
 * Toegang tot een dossier: intermediary/admin van het dossierkantoor, of een
 * dossierUser-koppeling én lidmaatschap van het dossierkantoor.
 */
function canViewDossier(sub) {
    return (q) => q.or(q.exists("agency", (q) => q.whereExists("appUsers", (q) => q
        .where("firebaseId", sub)
        .where("role", "IN", ["intermediary", "admin"]))), q.and(q.exists("dossierUser", (q) => q.whereExists("appUser", (q) => q.where("firebaseId", sub))), q.exists("agency", (q) => q.whereExists("appUsers", (q) => q.where("firebaseId", sub)))));
}
export const queries = defineQueries({
    preload: defineQuery(({ ctx }) => {
        return zql.agency
            .whereExists("appUsers", (q) => q.where("firebaseId", ctx?.sub ?? ""))
            .related("appUsers", (q) => q.where((q) => q.or(q.cmp("firebaseId", ctx?.sub ?? ""), q.exists("agency", (q) => q.whereExists("appUsers", (q) => q
            .where("firebaseId", ctx?.sub ?? "")
            .where("role", "IN", ["intermediary", "admin"]))))))
            .related("dossiers", (q) => q.where((q) => q.or(q.exists("agency", (q) => q.whereExists("appUsers", (q) => q
            .where("firebaseId", ctx?.sub ?? "")
            .where("role", "IN", ["intermediary", "admin"]))), q.exists("dossierUser", (q) => q.whereExists("appUser", (q) => q.where("firebaseId", ctx?.sub ?? ""))))))
            .related("dataRequest", (q) => q.whereExists("agency", (q) => q.whereExists("appUsers", (q) => q
            .where("firebaseId", ctx?.sub ?? "")
            .where("role", "IN", ["intermediary", "admin"]))))
            .related("conversations", (q) => q.whereExists("agency", (q) => q.whereExists("appUsers", (q) => q
            .where("firebaseId", ctx?.sub ?? "")
            .where("role", "IN", ["intermediary", "admin", "transcription"]))));
    }),
    agency: {
        current: defineQuery(({ ctx }) => {
            return zql.agency
                .whereExists("appUsers", (q) => q.where("firebaseId", ctx?.sub ?? ""))
                .one();
        }),
    },
    user: {
        me: defineQuery(({ ctx }) => {
            return zql.appUser.where("firebaseId", ctx?.sub ?? "").one();
        }),
        all: defineQuery(({ ctx }) => {
            return zql.appUser
                .whereExists("agency", (q) => q.whereExists("appUsers", (q) => q
                .where("firebaseId", ctx?.sub ?? "")
                .where("role", "IN", ["intermediary", "admin"])))
                .orderBy("fullName", "asc");
        }),
        byId: defineQuery(z.string(), ({ ctx, args: userId }) => {
            return zql.appUser
                .where("id", userId)
                .whereExists("agency", (q) => q.whereExists("appUsers", (q) => q
                .where("firebaseId", ctx?.sub ?? "")
                .where("role", "IN", ["intermediary", "admin"])))
                .one();
        }),
        availableAsOwner: defineQuery(z.object({
            dossierId: z.string(),
        }), ({ ctx, args: { dossierId } }) => {
            return zql.appUser
                .whereExists("agency", (q) => q.where((q) => q.and(q.exists("dossiers", (q) => q.where("id", dossierId)), q.exists("appUsers", (q) => q
                .where("firebaseId", ctx?.sub ?? "")
                .where("role", "IN", ["intermediary", "admin"])))))
                .where("role", "intermediary");
        }),
    },
    dossier: {
        all: defineQuery(({ ctx }) => {
            return zql.dossier
                .where((q) => q.or(q.exists("agency", (q) => q.whereExists("appUsers", (q) => q
                .where("firebaseId", ctx?.sub ?? "")
                .where("role", "IN", ["intermediary", "admin"]))), q.exists("dossierUser", (q) => q.whereExists("appUser", (q) => q.where("firebaseId", ctx?.sub ?? "")))))
                .orderBy("lastOpened", "desc");
        }),
        byId: defineQuery(z.object({
            field: z.union([z.literal("displayId"), z.literal("id")]),
            id: z.union([z.number(), z.string()]),
        }), ({ ctx, args: { field, id } }) => {
            return zql.dossier
                .where(field, id)
                .where((q) => q.or(q.exists("agency", (q) => q.whereExists("appUsers", (q) => q
                .where("firebaseId", ctx?.sub ?? "")
                .where("role", "IN", ["intermediary", "admin"]))), q.exists("dossierUser", (q) => q.whereExists("appUser", (q) => q.where("firebaseId", ctx?.sub ?? "")))))
                .related("versions", (q) => q.one())
                .one();
        }),
        dossierUsers: defineQuery(z.string(), ({ ctx, args: dossierId }) => {
            return zql.dossierUser
                .where("dossierId", dossierId)
                .whereExists("dossier", (q) => q.whereExists("agency", (q) => q.whereExists("appUsers", (q) => q
                .where("firebaseId", ctx?.sub ?? "")
                .where("role", "IN", ["intermediary", "admin"]))))
                .related("appUser");
        }),
    },
    conversations: {
        all: defineQuery(z.object({
            sortColumn: z.enum([
                "createdAt",
                "title",
                "generatedTitle",
                "displayId",
                "durationInSeconds",
                "status",
            ]),
            sortOrder: z.enum(["asc", "desc"]),
        }), ({ ctx, args: { sortColumn, sortOrder } }) => {
            return zql.conversationTable
                .whereExists("agency", (q) => q.whereExists("appUsers", (q) => q
                .where("firebaseId", ctx?.sub ?? "")
                .where("role", "IN", [
                "intermediary",
                "admin",
                "transcription",
            ])))
                .orderBy(sortColumn, sortOrder);
        }),
        byId: defineQuery(z.object({
            agencyId: z.string(),
            idField: z.union([z.literal("id"), z.literal("displayId")]),
            id: z.union([z.string(), z.number()]),
        }), ({ ctx, args: { agencyId, id, idField } }) => {
            return zql.conversationTable
                .where("agencyId", agencyId)
                .where(idField, id)
                .whereExists("agency", (q) => q.whereExists("appUsers", (q) => q
                .where("firebaseId", ctx?.sub ?? "")
                .where("role", "IN", [
                "intermediary",
                "admin",
                "transcription",
            ])))
                .one();
        }),
        byDossier: defineQuery(z.object({ dossierId: z.string() }), ({ ctx, args: { dossierId } }) => {
            return zql.conversationTable
                .where("dossierId", dossierId)
                .whereExists("agency", (q) => q.whereExists("appUsers", (q) => q
                .where("firebaseId", ctx?.sub ?? "")
                .where("role", "IN", [
                "intermediary",
                "admin",
                "transcription",
            ])))
                .orderBy("createdAt", "desc");
        }),
    },
    conversationDetails: {
        byConversationId: defineQuery(z.object({
            conversationId: z.string(),
        }), ({ ctx, args: { conversationId } }) => {
            return zql.conversationDetail
                .where("conversationId", conversationId)
                .whereExists("conversation", (q) => q.whereExists("agency", (q) => q.whereExists("appUsers", (q) => q
                .where("firebaseId", ctx?.sub ?? "")
                .where("role", "IN", [
                "intermediary",
                "admin",
                "transcription",
            ]))))
                .one();
        }),
    },
    dataRequests: {
        forCustomer: defineQuery(z.object({ dossierId: z.string(), customerId: z.string() }), ({ ctx, args: { dossierId, customerId } }) => {
            return zql.dataRequestTable
                .where((q) => q.and(q.cmp("parentDataRequestId", "IS", null), q.cmp("dossierId", dossierId), q.cmp("customerId", customerId)))
                .whereExists("agency", (q) => q.whereExists("appUsers", (q) => q
                .where("firebaseId", ctx?.sub ?? "")
                .where("role", "IN", ["intermediary", "admin"])))
                .related("children")
                .related("validations")
                .orderBy("createdAt", "desc");
        }),
        byId: defineQuery(z.object({ dataRequestId: z.string() }), ({ ctx, args: { dataRequestId } }) => {
            return zql.dataRequestTable
                .where("id", dataRequestId)
                .whereExists("agency", (q) => q.whereExists("appUsers", (q) => q
                .where("firebaseId", ctx?.sub ?? "")
                .where("role", "IN", ["intermediary", "admin"])))
                .one();
        }),
        hdnForDossierCustomer: defineQuery(z.object({ dossierId: z.string(), customerId: z.string() }), ({ ctx, args: { dossierId, customerId } }) => {
            return zql.dataRequestTable
                .where((q) => q.and(q.cmp("channel", "hdn"), q.cmp("dossierId", dossierId), q.cmp("customerId", customerId)))
                .whereExists("agency", (q) => q.whereExists("appUsers", (q) => q
                .where("firebaseId", ctx?.sub ?? "")
                .where("role", "IN", ["intermediary", "admin"])))
                .one();
        }),
    },
    dossierDocuments: {
        all: defineQuery(z.object({ dossierId: z.string() }), ({ ctx, args: { dossierId } }) => {
            return zql.dossierDocument
                .where("dossierId", dossierId)
                .whereExists("agency", (q) => q.whereExists("appUsers", (q) => q.where("firebaseId", ctx?.sub ?? "")))
                .whereExists("dossier", (q) => q.where(canViewDossier(ctx?.sub ?? "")));
        }),
    },
    dossierDocumentClassifications: {
        forDocument: defineQuery(z.object({ documentId: z.string() }), ({ ctx, args: { documentId } }) => {
            return zql.dossierDocumentClassification
                .where("documentId", documentId)
                .whereExists("agency", (q) => q.whereExists("appUsers", (q) => q.where("firebaseId", ctx?.sub ?? "")))
                .whereExists("dossier", (q) => q.where(canViewDossier(ctx?.sub ?? "")))
                .orderBy("classifiedAt", "desc")
                .limit(1);
        }),
        forDossier: defineQuery(z.object({ dossierId: z.string() }), ({ ctx, args: { dossierId } }) => {
            return zql.dossierDocumentClassification
                .where("dossierId", dossierId)
                .whereExists("agency", (q) => q.whereExists("appUsers", (q) => q.where("firebaseId", ctx?.sub ?? "")))
                .whereExists("dossier", (q) => q.where(canViewDossier(ctx?.sub ?? "")))
                .orderBy("classifiedAt", "desc");
        }),
    },
    dossierDocumentTokens: {
        withConversations: defineQuery(({ ctx }) => {
            return zql.dossierDocumentToken
                .whereExists("owner", (q) => q.where("firebaseId", ctx?.sub ?? ""))
                .related("dossierDocument", (q) => q.related("conversation"));
        }),
    },
    loanApplications: {
        forDossier: defineQuery(z.object({
            dossierId: z.string(),
            orderField: z.optional(z.enum(["createdAt"])),
            order: z.optional(z.enum(["asc", "desc"])),
        }), ({ ctx, args: { dossierId, orderField, order } }) => {
            let q = zql.hdnLoanApplication
                .where("dossierId", dossierId)
                .whereExists("agency", (q) => q.whereExists("appUsers", (q) => q
                .where("firebaseId", ctx?.sub ?? "")
                .where("role", "IN", ["intermediary", "admin"])))
                .related("requestedDocuments")
                .related("hdnDossier", (q) => q.related("provider"));
            if (orderField) {
                q = q.orderBy(orderField, order ?? "asc");
            }
            return q;
        }),
        byId: defineQuery(z.object({ loanApplicationId: z.string() }), ({ ctx, args: { loanApplicationId } }) => {
            return zql.hdnLoanApplication
                .where("id", loanApplicationId)
                .whereExists("agency", (q) => q.whereExists("appUsers", (q) => q
                .where("firebaseId", ctx?.sub ?? "")
                .where("role", "IN", ["intermediary", "admin"])))
                .related("requestedDocuments")
                .related("hdnDossier", (q) => q.related("provider"))
                .one();
        }),
    },
    loanOffers: {
        latestForApplication: defineQuery(z.object({ loanApplicationId: z.string() }), ({ ctx, args: { loanApplicationId } }) => {
            return zql.loanOfferTable
                .where("loanApplicationId", loanApplicationId)
                .whereExists("application", (q) => q.whereExists("agency", (q) => q.whereExists("appUsers", (q) => q
                .where("firebaseId", ctx?.sub ?? "")
                .where("role", "IN", ["intermediary", "admin"]))))
                .related("documents")
                .orderBy("createdAt", "desc")
                .one();
        }),
    },
    providers: {
        preload: defineQuery(({ ctx }) => {
            return zql.hdnProvider
                .related("supportedServiceProviders", (cb) => cb.related("connections"))
                .related("connections", (cb) => cb.whereExists("agency", (q) => q.whereExists("appUsers", (cb) => cb
                .where("firebaseId", ctx?.sub ?? "")
                .where("role", "IN", ["intermediary", "admin"]))));
        }),
        all: defineQuery(() => {
            return zql.hdnProvider.related("hdnReceiver", (q) => q.related("hdnReceiverNode").related("hdnReceiverFeature"));
        }),
        activeMortgage: defineQuery(z.object({ agencyId: z.string() }), ({ ctx, args: { agencyId } }) => {
            return zql.hdnProvider
                .where("receiverCode", "IS NOT", null)
                .where("isActive", true)
                .where("mortgage", true)
                .related("supportedServiceProviders", (cb) => cb.whereExists("connections", (cb) => cb.where((q) => q.and(q.exists("agency", (q) => q.whereExists("appUsers", (cb) => cb
                .where("firebaseId", ctx?.sub ?? "")
                .where("role", "IN", ["intermediary", "admin"]))), q.cmp("agencyId", agencyId), q.cmp("connectionNumber", "IS NOT", null)))))
                .related("connections", (cb) => cb
                .whereExists("agency", (q) => q.whereExists("appUsers", (cb) => cb
                .where("firebaseId", ctx?.sub ?? "")
                .where("role", "IN", ["intermediary", "admin"])))
                .related("serviceProvider"))
                .orderBy("name", "asc");
        }),
    },
    lenders: {
        all: defineQuery(() => {
            return zql.lender.where("isActive", true).orderBy("name", "asc");
        }),
    },
    products: {
        forProvider: defineQuery(z.object({ providerCode: z.string() }), ({ args: { providerCode } }) => {
            return zql.hdnProviderProduct
                .where("providerCode", providerCode)
                .where("isActive", true);
        }),
    },
    partProducts: {
        forProvider: defineQuery(z.object({ providerCode: z.string() }), ({ args: { providerCode } }) => {
            return zql.hdnProviderProductPart
                .where("providerCode", providerCode)
                .where("isActive", true);
        }),
    },
    receivers: {
        datakeeperSupported: defineQuery(() => {
            return zql.hdnReceiverBrondataleverancier
                .where((q) => q.and(q.cmp("brondataleverancier", "08 Datakeeper"), q.cmp("isSupported", true)))
                .whereExists("hdnReceiver")
                .related("hdnReceiver", (q) => q.whereExists("hdnProvider").related("hdnProvider", (q) => q.one()));
        }),
    },
    requestedDocuments: {
        forLoanApplication: defineQuery(z.object({ hdnLoanApplicationId: z.string() }), ({ ctx, args: { hdnLoanApplicationId } }) => {
            return zql.hdnRequestedDocument
                .where("hdnLoanApplicationId", hdnLoanApplicationId)
                .whereExists("agency", (q) => q.whereExists("appUsers", (q) => q
                .where("firebaseId", ctx?.sub ?? "")
                .where("role", "IN", ["intermediary", "admin"])))
                .related("ketenpartijStatus")
                .related("documentStatus")
                .related("sentDocumentAction", (q) => q.related("document"))
                .related("linkedDocumentAction", (q) => q.related("document"))
                .related("partijNawData");
        }),
    },
    serviceProviders: {
        all: defineQuery(() => {
            return zql.hdnServiceProvider;
        }),
        withConnections: defineQuery(z.object({ agencyId: z.string() }), ({ ctx, args: { agencyId } }) => {
            return zql.hdnServiceProvider
                .related("connections", (cb) => cb.where((q) => q.and(q.exists("agency", (q) => q.where((q) => q.and(q.cmp("id", agencyId), q.exists("appUsers", (q) => q
                .where("firebaseId", ctx?.sub ?? "")
                .where("role", "IN", ["intermediary", "admin"]))))), q.cmp("connectionNumber", "IS NOT", null))))
                .related("availableProviders", (cb) => cb.related("connections", (cb) => cb.where((q) => q.and(q.exists("agency", (q) => q.where((q) => q.and(q.cmp("id", agencyId), q.exists("appUsers", (q) => q
                .where("firebaseId", ctx?.sub ?? "")
                .where("role", "IN", ["intermediary", "admin"]))))), q.or(q.cmp("connectionNumber", "IS NOT", null), q.cmp("serviceProviderId", "IS NOT", null))))))
                .orderBy("name", "asc");
        }),
    },
    inboundEmails: {
        byId: defineQuery(z.object({ inboundEmailId: z.string() }), ({ args: { inboundEmailId } }) => {
            return zql.inboundEmailTable.where("id", inboundEmailId).one();
        }),
    },
    interestEmails: {
        all: defineQuery(() => {
            return zql.interestEmailTable.orderBy("receivedAt", "desc");
        }),
    },
    interestSheets: {
        all: defineQuery(() => {
            return zql.interestSheet
                .related("document", (q) => q.one())
                .orderBy("createdAt", "desc");
        }),
        byId: defineQuery(z.object({ interestSheetId: z.string() }), ({ args: { interestSheetId } }) => {
            return zql.interestSheet
                .where("id", interestSheetId)
                .related("document", (q) => q.one())
                .related("detail", (q) => q.one())
                .one();
        }),
        forEmail: defineQuery(z.object({ interestEmailId: z.string() }), ({ args: { interestEmailId } }) => {
            return zql.interestSheet
                .where("interestEmailId", interestEmailId)
                .related("document", (q) => q.one())
                .orderBy("createdAt", "asc");
        }),
    },
    dossierVersionNotes: {
        byVersionId: defineQuery(z.object({ versionId: z.string() }), ({ ctx, args: { versionId } }) => {
            return zql.dossierVersionNotes
                .where("versionId", versionId)
                .whereExists("version", (q) => q.whereExists("dossier", (q) => q.whereExists("agency", (q) => q.whereExists("appUsers", (q) => q
                .where("firebaseId", ctx?.sub ?? "")
                .where("role", "IN", ["intermediary", "admin"])))))
                .one();
        }),
    },
    informatieAanvragen: {
        forDossier: defineQuery(z.object({ dossierId: z.string() }), ({ ctx, args: { dossierId } }) => {
            return zql.hdnInformatieAanvraagTable
                .where("dossierId", dossierId)
                .whereExists("agency", (q) => q.whereExists("appUsers", (q) => q
                .where("firebaseId", ctx?.sub ?? "")
                .where("role", "IN", ["intermediary", "admin"])))
                .related("hdnDossier")
                .orderBy("createdAt", "desc");
        }),
    },
});
